import { Header } from '../Header.js';
import { TabNavigation } from '../TabNavigation.js';
import { NotificationManager } from '../../utils/notifications.js';
import { MenuItemModal } from '../MenuItemModal.js';
import { AnimationManager } from '../../services/AnimationManager.js';
import { setupResponsiveGrid } from '../../utils/gridColumns.js';

export class TopBarTabsLayout {
  constructor(config, menuData, cartCount = 0, i18n = null) {
    this.config = config;
    this.menuData = menuData;
    this.cartCount = cartCount;
    this.i18n = i18n;

    this.featuredItems = this.getFeaturedItems();
    if (!this.menuData.items) {
      this.menuData.items = {};
    }
    if (this.featuredItems.length > 0) {
      this.menuData.items.featured = this.featuredItems;
    }

    this.categoriesList = this.buildCategories();
    this.currentCategory = this.categoriesList[0]?.id || (this.menuData.categories?.[0]?.id) || 'featured';

    this.header = new Header(this.cartCount, this.config);
    this.tabNavigation = new TabNavigation(this.categoriesList);
    this.modal = new MenuItemModal(this.i18n);
    this.animationManager = new AnimationManager();

    this.columnsPerRow = this.config?.kioskDisplaySettings?.layoutPreferences?.columnsPerRow || 3;
  }

  render() {
    return `
      <div class="main-container" style="display: block;">
        ${this.header.render()}
        ${this.tabNavigation.render()}
        <div class="content-container">
          ${this.renderTabContents()}
        </div>
      </div>
    `;
  }

  renderTabContents() {
    return this.categoriesList.map(category => `
      <div class="tab-content ${category.id === this.currentCategory ? 'active' : ''}"
           id="${category.id}">
        <div class="menu-items-grid" data-category-grid="${category.id}">
          ${this.renderMenuItems(category.id)}
        </div>
      </div>
    `).join('');
  }

  renderMenuItems(categoryId) {
    const items = this.menuData.items[categoryId] || [];
    const availableItems = items.filter(item => item.isAvailable !== false);
    return availableItems.map(item => {
      const images = item.images || (item.image ? [item.image] : []);
      const cardClasses = ['menu-item-card'];
      if (this.isFeatured(item)) {
        cardClasses.push('is-featured');
      }

      return `
      <div class="${cardClasses.join(' ')}" data-item="${item.id}">
        <div class="item-image-container">
          ${images.length > 0 ?
            `<img src="${images[0]}" alt="${item.name}" class="item-image">` :
            `<div class="item-image-placeholder">
              <i class="fas fa-utensils"></i>
            </div>`}

          ${images.length > 0 ? (item.isImageGenerated ? `
            <div class="ai-generated-watermark">
              <span class="watermark-text">ZDJĘCIE WYGENEROWANE</span>
            </div>
          ` : `
            <div class="preview-image-watermark">
              <span class="watermark-text">ZDJĘCIE POGLĄDOWE</span>
            </div>
          `) : ''}

          ${this.renderBadge(item)}
          ${item.dietary && item.dietary.length > 0 ? `
            <div class="dietary-icons">
              ${item.dietary.map(diet => `
                <div class="dietary-icon ${diet}">
                  <i class="${this.getDietaryIcon(diet)}"></i>
                </div>
              `).join('')}
            </div>
          ` : ''}
        </div>
        <div class="item-content">
          <h3 class="item-name">${item.name}</h3>
          <p class="item-description">${item.description}</p>
          <div class="item-footer">
            <div class="item-price">${item.currency || this.config.settings?.currency || 'PLN'} ${typeof item.price === 'object' ? item.price.amount : (item.price || 0)}</div>
            <button class="add-to-cart" data-item="${item.id}">
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>
      </div>
    `;
    }).join('');
  }

  t(key, fallback) {
    if (!this.i18n || typeof this.i18n.t !== 'function') {
      return fallback;
    }
    const translation = this.i18n.t(key);
    return translation === key ? fallback : translation;
  }

  getFeaturedLabel() {
    return this.t('menu.featured', 'Featured');
  }

  buildCategories() {
    const base = Array.isArray(this.menuData.categories) ? [...this.menuData.categories] : [];
    if (this.featuredItems.length > 0) {
      const label = this.getFeaturedLabel();
      return [
        {
          id: 'featured',
          name: label,
          icon: 'fa-star',
          description: label
        },
        ...base
      ];
    }
    return base;
  }

  getFeaturedItems() {
    const collected = new Map();

    if (Array.isArray(this.menuData?.specials)) {
      this.menuData.specials.forEach(item => {
        if (item && item.id) {
          collected.set(item.id, item);
        }
      });
    }

    if (this.menuData?.items) {
      Object.values(this.menuData.items).forEach(list => {
        (list || []).forEach(item => {
          if (item && item.id && this.isFeatured(item)) {
            collected.set(item.id, item);
          }
        });
      });
    }

    return Array.from(collected.values()).sort((a, b) => {
      const orderA = a.displayOrder ?? 0;
      const orderB = b.displayOrder ?? 0;
      if (orderA === orderB) {
        return a.name.localeCompare(b.name);
      }
      return orderA - orderB;
    });
  }

  isFeatured(item) {
    if (!item) return false;
    const badge = (item.badge || '').toString().toLowerCase();
    if (badge === 'featured' || badge === 'recommended') {
      return true;
    }
    const status = (item.status || '').toString().toLowerCase();
    if (status === 'recommended') {
      return true;
    }
    if (item.isPromotedToday || item.metadata?.isPromotedToday) {
      return true;
    }
    return false;
  }

  renderBadge(item) {
    if (!item) return '';
    const badgeKey = (item.badge || '').toString().toLowerCase();
    const label = item.badgeText || this.t('menu.badge.recommended', 'Recommended');

    if (this.isFeatured(item)) {
      return `
        <div class="badge-corner" aria-label="${label}">
          <span class="badge-star" aria-hidden="true">★</span>
          <span class="badge-label">${label}</span>
        </div>
      `;
    }

    if (badgeKey) {
      return `<span class="badge ${item.badge}">${item.badgeText || item.badge}</span>`;
    }
    return '';
  }

  getDietaryIcon(dietary) {
    const icons = {
      vegetarian: 'fas fa-leaf',
      vegan: 'fas fa-seedling',
      'gluten-free': 'fas fa-wheat',
      spicy: 'fas fa-pepper-hot'
    };
    return icons[dietary] || 'fas fa-circle';
  }

  bindEvents() {
    const menuGrids = document.querySelectorAll('.menu-items-grid[data-category-grid]');
    menuGrids.forEach(grid => {
      setupResponsiveGrid(grid, this.columnsPerRow);
    });

    this.header.bindEvents(
      () => this.handleSearch(),
      () => this.handleCartClick(),
      () => this.handleLanguageClick(),
      () => this.handleStaffCallClick(),
      () => this.handleLogoClick()
    );

    this.tabNavigation.bindEvents(
      (categoryId) => this.handleTabChange(categoryId)
    );

    this.bindItemClickEvents();

    const addToCartBtns = document.querySelectorAll('.add-to-cart');
    addToCartBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = btn.getAttribute('data-item');
        this.handleAddToCart(itemId, btn);
      });
    });

    this.bindImageNavigation();
  }

  bindItemClickEvents() {
    const menuItemCards = document.querySelectorAll('.menu-item-card');
    menuItemCards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.add-to-cart') || e.target.closest('.image-nav-btn')) {
          return;
        }

        const itemId = card.getAttribute('data-item');
        const item = this.findItemById(itemId);
        if (item) {
          const categoryItems = this.menuData.items[this.currentCategory] || [];
          const availableCategoryItems = categoryItems.filter(item => item.isAvailable !== false);
          this.modal.show(item, availableCategoryItems, this.config);
        }
      });
    });
  }

  async handleTabChange(categoryId) {
    if (this.currentCategory === categoryId) return;

    const oldCategory = this.currentCategory;
    this.currentCategory = categoryId;

    const oldContent = document.getElementById(oldCategory);
    const newContent = document.getElementById(categoryId);

    const selectedTabButton = document.querySelector(`[data-tab="${categoryId}"]`);
    if (selectedTabButton) {
      this.animationManager.animateCategorySelection(selectedTabButton);
    }

    await this.animationManager.animateCategoryTransition(oldContent, newContent, 'fade');

    document.querySelectorAll('.tab-content').forEach(content => {
      content.classList.remove('active');
    });

    if (newContent) {
      newContent.classList.add('active');

      const menuGrid = newContent.querySelector('.menu-items-grid');
      if (menuGrid) {
        menuGrid.style.opacity = '0';
        menuGrid.style.transition = 'opacity 0.3s ease-in-out';

        menuGrid.innerHTML = this.renderCategoryItems(categoryId);
        setupResponsiveGrid(menuGrid, this.columnsPerRow);

        setTimeout(() => {
          menuGrid.style.opacity = '1';
          this.animationManager.animateMenuItems(menuGrid, 30);

          this.bindItemClickEvents();
          this.bindAddToCartEvents();
        }, 50);
      }
    }

    const contentContainer = document.querySelector('.content-container');
    if (contentContainer) {
      this.animationManager.smoothScrollToTop(contentContainer);
    }
  }

  handleAddToCart(itemId, buttonElement) {
    const item = this.findItemById(itemId);
    if (!item) {
      console.error('Item not found:', itemId);
      return;
    }

    const cartItem = {
      id: item.id,
      name: item.name,
      price: item.price?.amount || item.price || 0,
      quantity: 1,
      image: item.image || item.imageUrl,
      currency: item.currency || this.config.settings?.currency || this.config.currency || 'PLN',
      modifiers: {},
      specialInstructions: '',
      totalPrice: item.price?.amount || item.price || 0
    };

    const event = new CustomEvent('itemAddedToCart', {
      detail: cartItem
    });
    document.dispatchEvent(event);

    this.animateAddToCartButton(buttonElement);
  }

  /**
   * Animate add-to-cart button with satisfying feedback
   */
  animateAddToCartButton(buttonElement) {
    buttonElement.innerHTML = '<i class="fas fa-check"></i>';
    buttonElement.style.background = 'var(--color-success, #3E885B)';
    buttonElement.style.color = 'white';
    buttonElement.style.transform = 'scale(1.2)';
    buttonElement.style.transition = 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)';

    setTimeout(() => {
      buttonElement.style.transform = 'scale(1.1)';
    }, 150);

    setTimeout(() => {
      buttonElement.innerHTML = '<i class="fas fa-plus"></i>';
      buttonElement.style.background = '';
      buttonElement.style.color = '';
      buttonElement.style.transform = '';
      buttonElement.style.transition = 'all 0.3s ease';
    }, 800);
  }

  handleSearch() {
    NotificationManager.info('Search functionality coming soon!');
  }

  handleCartClick() {
    const event = new CustomEvent('showOrderConfirmation');
    document.dispatchEvent(event);
  }

  handleLanguageClick() {
    const event = new CustomEvent('showLanguageSelector');
    document.dispatchEvent(event);
  }

  handleStaffCallClick() {
    const event = new CustomEvent('showStaffCallModal');
    document.dispatchEvent(event);
  }

  handleLogoClick() {
    const event = new CustomEvent('navigateToWelcome');
    document.dispatchEvent(event);
  }

  bindImageNavigation() {
    const imageNavBtns = document.querySelectorAll('.image-nav-btn');
    imageNavBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const direction = btn.getAttribute('data-direction');
        const card = btn.closest('.menu-item-card');
        this.switchImage(card, direction);
      });
    });
  }

  switchImage(card, direction) {
    const container = card.querySelector('.item-image-container');
    const img = container.querySelector('.item-image');
    const indicator = card.querySelector('.image-indicator');

    if (!img || !img.dataset.images) return;

    const images = JSON.parse(img.dataset.images);
    const currentIndex = parseInt(container.dataset.currentImage || '0');

    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % images.length;
    } else {
      newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    }

    img.src = images[newIndex];
    container.dataset.currentImage = newIndex;

    if (indicator) {
      indicator.textContent = `${newIndex + 1} of ${images.length}`;
    }
  }

  findItemById(itemId) {
    for (const categoryItems of Object.values(this.menuData.items)) {
      const item = categoryItems.find(item => item.id === itemId);
      if (item) return item;
    }
    return null;
  }

  show() {
    const menuContainer = document.querySelector('.main-container');
    if (menuContainer) {
      menuContainer.style.display = 'block';
      menuContainer.style.opacity = '0';

      menuContainer.style.transform = 'translateX(100%)';
      menuContainer.style.transition = 'all 0.5s ease';

      requestAnimationFrame(() => {
        menuContainer.style.opacity = '1';
        menuContainer.style.transform = 'translateX(0)';

        setTimeout(() => {
          this.loadInitialCategoryWithAnimations();
        }, 600);
      });
    }
  }

  loadInitialCategoryWithAnimations() {
    const activeTabContent = document.querySelector('.tab-content.active');
    if (activeTabContent) {
      const menuGrid = activeTabContent.querySelector('.menu-items-grid');
      if (menuGrid) {
        this.animationManager.animateMenuItems(menuGrid, 50);
      }
    }
  }

  hide() {
    return new Promise((resolve) => {
      const menuContainer = document.querySelector('.main-container');
      if (menuContainer) {
        menuContainer.style.transition = 'all 0.5s ease';
        menuContainer.style.opacity = '0';
        menuContainer.style.transform = 'translateX(-100%)';

        setTimeout(() => {
          menuContainer.style.display = 'none';
          resolve();
        }, 500);
      } else {
        resolve();
      }
    });
  }

  renderCategoryItems(categoryId) {
    const items = this.menuData.items[categoryId] || [];
    const availableItems = items.filter(item => item.isAvailable !== false);
    return availableItems.map(item => {
      const images = item.images || (item.image ? [item.image] : []);
      return `
        <div class="menu-item-card" data-item="${item.id}">
          <div class="item-image-container">
            ${images.length > 0 ?
              `<img src="${images[0]}" alt="${item.name}" class="item-image">` :
              `<div class="item-image-placeholder">
                <i class="fas fa-utensils"></i>
              </div>`}

            ${images.length > 0 ? (item.isImageGenerated ? `
              <div class="ai-generated-watermark">
                <span class="watermark-text">ZDJĘCIE WYGENEROWANE</span>
              </div>
            ` : `
              <div class="preview-image-watermark">
                <span class="watermark-text">ZDJĘCIE POGLĄDOWE</span>
              </div>
            `) : ''}

            ${item.badge ? `<span class="badge ${item.badge}">${item.badgeText || item.badge}</span>` : ''}
          </div>
          <div class="item-content">
            <h3 class="item-name">${item.name}</h3>
            <p class="item-description">${item.description}</p>
            <div class="item-footer">
              <div class="item-price">${item.price}</div>
              <button class="add-to-cart" data-item="${item.id}">
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
        </div>
      `;
    }).join('');
  }

  bindAddToCartEvents() {
    const addToCartBtns = document.querySelectorAll('.add-to-cart');
    addToCartBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = btn.getAttribute('data-item');
        this.handleAddToCart(itemId, btn);
      });
    });
  }

  updateCartCount(count) {
    const oldCount = this.cartCount;
    this.cartCount = count;

    if (this.header) {
      this.header.updateCartCount(count);
    }

    const cartCountElement = document.querySelector('.cart-count');
    if (cartCountElement && this.animationManager) {
      this.animationManager.animateCounter(cartCountElement, oldCount, count, 400);
    }

    const cartButton = document.querySelector('.cart-btn');
    if (cartButton && this.animationManager) {
      this.animationManager.addSpringBounce(cartButton);
    }
  }

  getSupportedFeatures() {
    return ['search', 'cart', 'tabNavigation', 'categories'];
  }
}
